import type { ReactElement } from 'react';
import { Area, AreaChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { formatCompact } from '@/shared/analytics/MetricFormatting';
import type { ModelMixPoint, ModelMixSeries } from './InsightsTypes';

interface InsightsModelMixChartProps {
    models: ModelMixSeries[];
    points: ModelMixPoint[];
    /** The selected window — every day in `[startIso, endIso]` is plotted, zero-filled where there's no data, so the axis matches the range control. */
    startIso: string;
    endIso: string;
}

const DAY_MS = 86_400_000;

type MixRow = Record<string, number | string>;

function eachDay(startDay: string, endDay: string): string[] {
    const out: string[] = [];
    let t = Date.parse(`${startDay}T00:00:00Z`);
    const end = Date.parse(`${endDay}T00:00:00Z`);
    for (; t <= end; t += DAY_MS) {
        out.push(new Date(t).toISOString().slice(0, 10));
    }
    return out;
}

function toRows(models: ModelMixSeries[], points: ModelMixPoint[], startDay: string, endDay: string): MixRow[] {
    const byDate = new Map(points.map((point) => [point.date, point]));
    return eachDay(startDay, endDay).map((date) => {
        const point = byDate.get(date);
        const row: MixRow = { date: new Date(`${date}T12:00:00`).toLocaleDateString(undefined, { month: 'short', day: 'numeric' }) };
        models.forEach((model, i) => {
            row[model.key] = point?.values[i] ?? 0;
        });
        return row;
    });
}

/** Stacked token volume per model across the selected window (zero-filled on days with no data). */
export default function InsightsModelMixChart({ models, points, startIso, endIso }: InsightsModelMixChartProps): ReactElement {
    const rows = toRows(models, points, startIso.slice(0, 10), endIso.slice(0, 10));
    return (
        <ResponsiveContainer width="100%" height={220}>
            <AreaChart data={rows} margin={{ top: 4, right: 8, bottom: 0, left: 0 }}>
                <CartesianGrid vertical={false} stroke="var(--bm-color-border-subtle)" />
                <XAxis dataKey="date" tick={{ fill: 'var(--bm-color-text-dimmed)', fontSize: 10 }} tickLine={false} axisLine={false} minTickGap={28} />
                <YAxis tickFormatter={(v: number) => formatCompact(v)} tick={{ fill: 'var(--bm-color-text-dimmed)', fontSize: 10 }} tickLine={false} axisLine={false} width={44} />
                <Tooltip
                    formatter={(value: number, key: string) => [formatCompact(value), models.find((m) => m.key === key)?.label ?? key]}
                    contentStyle={{ background: 'var(--bm-color-surface)', border: '1px solid var(--bm-color-border)', borderRadius: 'var(--bm-radius)', fontSize: 12 }}
                />
                {models.map((model, i) => {
                    const color = `var(--bm-chart-${(i % 8) + 1})`;
                    return <Area key={model.key} type="monotone" dataKey={model.key} stackId="mix" name={model.label} stroke={color} fill={color} fillOpacity={0.28} strokeWidth={1.5} />;
                })}
            </AreaChart>
        </ResponsiveContainer>
    );
}
